import React, { useEffect, useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import TablePagination from '@mui/material/TablePagination';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Autocomplete from '@mui/material/Autocomplete';
import Grid from '@mui/material/Grid';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import InputAdornment from '@mui/material/InputAdornment';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Plus,
  Search,
  FileText,
  CheckCircle2,
  XCircle,
  CalendarClock as HrInterviewsLogoIcon
} from 'lucide-react';
import { toast } from 'react-toastify';
import api from '../services/api';

const StyledCard = styled(Card)(({ theme }) => ({
  boxShadow: '0 0 2px 0 rgba(145, 158, 171, 0.2), 0 12px 24px -4px rgba(145, 158, 171, 0.12)',
  borderRadius: 16,
  overflow: 'hidden'
}));

const statoOptions = [
  { value: 'programmato', label: 'Programmato' },
  { value: 'sostenuto', label: 'Sostenuto' },
  { value: 'chiuso', label: 'Chiuso' }
];

const esitoOptionsBase = [
  { value: 'positivo', label: 'Positivo' },
  { value: 'negativo', label: 'Negativo' },
  { value: 'da_approfondire', label: 'Da approfondire' },
  { value: 'secondo_colloquio', label: 'Secondo colloquio' },
  { value: 'offerta_fatta', label: 'Offerta fatta' },
  { value: 'offerta_rifiutata', label: 'Offerta rifiutata' }
];

const legacyStatoToEsito = {
  assunto: 'positivo',
  non_idoneo: 'negativo',
  rifiutato: 'offerta_rifiutata'
};

const normalizeStatoValue = (value) => {
  const v = value || 'programmato';
  if (v === 'assunto' || v === 'non_idoneo' || v === 'rifiutato') return 'chiuso';
  return v;
};

const normalizeEsitoValue = (stato, esito) => {
  if (esito) return esito;
  return legacyStatoToEsito[stato] || '';
};

const formatEsitoLabel = (value) => {
  const item = esitoOptionsBase.find((opt) => opt.value === value);
  return item ? item.label : value;
};

export default function HrColloqui() {
  const location = useLocation();
  const navigate = useNavigate();
  const autoOpenedRef = useRef(false);
  const returnToCandidatesRef = useRef(false);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statoFilter, setStatoFilter] = useState('programmato');
  const [candidatoFilterId, setCandidatoFilterId] = useState('');

  const [filterFonteId, setFilterFonteId] = useState('');
  const [filterPosizioneId, setFilterPosizioneId] = useState('');
  const [fromDate, setFromDate] = useState('');
  const [untilDate, setUntilDate] = useState('');

  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('data_colloquio');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const [candidatoId, setCandidatoId] = useState('');
  const [posizioneId, setPosizioneId] = useState('');
  const [fonteId, setFonteId] = useState('');
  const [dataColloquio, setDataColloquio] = useState('');
  const [oraInizio, setOraInizio] = useState('');
  const [oraFine, setOraFine] = useState('');
  const [luogo, setLuogo] = useState('');
  const [stato, setStato] = useState('programmato');
  const [esito, setEsito] = useState('');
  const [note, setNote] = useState('');

  const [candidati, setCandidati] = useState([]);
  const [posizioni, setPosizioni] = useState([]);
  const [fonti, setFonti] = useState([]);
  const [autoCreateCandidateId, setAutoCreateCandidateId] = useState('');

  const shouldShowEsito = stato === 'sostenuto' || stato === 'chiuso';

  const esitoOptionsForForm = useMemo(() => {
    const opts = [...esitoOptionsBase];
    if (esito && !opts.some((o) => o.value === esito)) {
      opts.unshift({ value: esito, label: `Altro: ${esito}` });
    }
    return opts;
  }, [esito]);

  const fetchReferences = async () => {
    try {
      const [cRes, pRes, fRes] = await Promise.all([
        api.get('/hr/candidati'),
        api.get('/hr/posizioni', { params: { onlyActive: 1 } }),
        api.get('/hr/fonti', { params: { onlyActive: 1 } })
      ]);
      setCandidati(cRes.data || []);
      setPosizioni(pRes.data || []);
      setFonti(fRes.data || []);
    } catch (error) {
      console.error('Error fetching HR reference data:', error);
      toast.error('Errore nel caricamento dei dati di supporto');
    }
  };

  const fetchRows = async () => {
    try {
      setLoading(true);
      const params = {};
      if (statoFilter) {
        params.stato = statoFilter;
      }
      if (candidatoFilterId) {
        params.candidato_id = candidatoFilterId;
      }
      if (filterPosizioneId) {
        params.posizione_ids = filterPosizioneId;
      }
      if (filterFonteId) {
        params.fonte_ids = filterFonteId;
      }
      if (fromDate) {
        params.from = fromDate;
      }
      if (untilDate) {
        params.until = untilDate;
      }
      const res = await api.get('/hr/colloqui', { params });
      setRows(res.data || []);
    } catch (error) {
      console.error('Error fetching colloqui:', error);
      toast.error('Errore nel caricamento dei colloqui');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReferences();
  }, []);

  useEffect(() => {
    fetchRows();
  }, [statoFilter, candidatoFilterId, filterFonteId, filterPosizioneId, fromDate, untilDate]);

  useEffect(() => {
    if (dialogOpen || editingItem) return;
    if (autoOpenedRef.current) return;

    const params = new URLSearchParams(location.search || '');
    const stateOpenNew = !!location.state?.openNew;
    const stateCandidateId = location.state?.candidatoId ? String(location.state.candidatoId) : '';
    const stateReturnToCandidates = !!location.state?.returnToCandidates;

    const shouldOpen = stateOpenNew || params.get('openNew') === '1';
    const candidateFromUrl = params.get('candidatoId');
    const candidateIdToUse = stateCandidateId || candidateFromUrl;

    if (shouldOpen && candidateIdToUse) {
      autoOpenedRef.current = true;
      returnToCandidatesRef.current = stateReturnToCandidates;
      openCreateDialog();
      setCandidatoId(candidateIdToUse);
      setAutoCreateCandidateId(candidateIdToUse);
      if (location.search) {
        navigate({ pathname: location.pathname, search: '' }, { replace: true, state: null });
      }
    }
  }, [location, dialogOpen, editingItem, navigate]);

  useEffect(() => {
    const params = new URLSearchParams(location.search || '');
    const stateFilterCandidateId = location.state?.candidatoIdFilter
      ? String(location.state.candidatoIdFilter)
      : '';
    const candidateIdFromUrl = params.get('candidatoId');
    const idToUse = stateFilterCandidateId || candidateIdFromUrl || '';

    if (idToUse) {
      setCandidatoFilterId(idToUse);
      setStatoFilter('');
    }
  }, [location.search, location.state]);

  useEffect(() => {
    if (!autoCreateCandidateId) return;
    const candidate = candidati.find((c) => String(c.id) === String(autoCreateCandidateId));
    if (!candidate) return;
    setPosizioneId(candidate.posizione_id || '');
    setFonteId(candidate.fonte_id || '');
    setAutoCreateCandidateId('');
  }, [autoCreateCandidateId, candidati]);

  const filteredRows = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return rows;
    return rows.filter((row) => {
      const parts = [
        row.candidato_cognome,
        row.candidato_nome,
        row.candidato_email,
        row.candidato_telefono,
        row.posizione_titolo,
        row.fonte_nome,
        row.luogo,
        row.esito,
        row.note
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return parts.includes(term);
    });
  }, [rows, search]);

  const sortedRows = useMemo(() => {
    const arr = [...filteredRows];
    arr.sort((a, b) => {
      const valueA = a[orderBy];
      const valueB = b[orderBy];
      if (valueA === null || valueA === undefined) return 1;
      if (valueB === null || valueB === undefined) return -1;
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return order === 'asc' ? valueA - valueB : valueB - valueA;
      }
      const strA = String(valueA).toLowerCase();
      const strB = String(valueB).toLowerCase();
      if (strA < strB) return order === 'asc' ? -1 : 1;
      if (strA > strB) return order === 'asc' ? 1 : -1;
      return 0;
    });
    return arr;
  }, [filteredRows, order, orderBy]);

  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const openCreateDialog = () => {
    setSubmitAttempted(false);
    setEditingItem(null);
    setCandidatoId('');
    setPosizioneId('');
    setFonteId('');
    setDataColloquio('');
    setOraInizio('');
    setOraFine('');
    setLuogo('');
    setStato('programmato');
    setEsito('');
    setNote('');
    setDialogOpen(true);
  };

  const openEditDialog = (item) => {
    setSubmitAttempted(false);
    returnToCandidatesRef.current = false;
    const legacyStato = item.stato || 'programmato';
    const normalizedStato = normalizeStatoValue(legacyStato);
    const normalizedEsito = normalizeEsitoValue(legacyStato, item.esito || '');
    setEditingItem(item);
    setCandidatoId(item.candidato_id || '');
    setPosizioneId(item.posizione_id || '');
    setFonteId(item.fonte_id || '');
    setDataColloquio(item.data_colloquio ? String(item.data_colloquio).slice(0, 10) : '');
    setOraInizio(item.ora_inizio ? String(item.ora_inizio).slice(0, 5) : '');
    setOraFine(item.ora_fine ? String(item.ora_fine).slice(0, 5) : '');
    setLuogo(item.luogo || '');
    setStato(normalizedStato);
    setEsito(normalizedEsito);
    setNote(item.note || '');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    setSubmitAttempted(true);
    if (!candidatoId) {
      toast.error('Il candidato è obbligatorio');
      return;
    }
    if (stato === 'chiuso' && !esito) {
      toast.error('Seleziona un esito per chiudere il colloquio');
      return;
    }

    const payload = {
      candidato_id: candidatoId,
      posizione_id: posizioneId || null,
      fonte_id: fonteId || null,
      data_colloquio: dataColloquio || null,
      ora_inizio: oraInizio || null,
      ora_fine: oraFine || null,
      luogo: luogo || null,
      stato,
      esito: shouldShowEsito ? (esito || null) : null,
      note: note || null
    };

    try {
      setSaving(true);
      const isCreate = !editingItem;
      if (editingItem) {
        await api.put(`/hr/colloqui/${editingItem.id}`, payload);
        toast.success('Colloquio aggiornato');
      } else {
        await api.post('/hr/colloqui', payload);
        toast.success('Colloquio creato');
      }
      setDialogOpen(false);
      setEditingItem(null);
      if (isCreate && returnToCandidatesRef.current) {
        returnToCandidatesRef.current = false;
        navigate('/hr/candidati', { replace: true, state: { highlightCandidateId: candidatoId } });
        return;
      }
      navigate({ pathname: location.pathname, search: '' }, { replace: true, state: null });
      await fetchRows();
    } catch (error) {
      console.error('Error saving colloquio:', error);
      const message =
        error.response?.data?.message || 'Errore nel salvataggio del colloquio';
      toast.error(message);
    } finally {
      setSaving(false);
    }
  };

  const openDeleteDialog = (item) => {
    setDeleteTarget(item);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget?.id) return;
    try {
      setDeleteLoading(true);
      await api.delete(`/hr/colloqui/${deleteTarget.id}`);
      toast.success('Colloquio eliminato');
      setDeleteDialogOpen(false);
      setDeleteTarget(null);
      await fetchRows();
    } catch (error) {
      console.error('Error deleting colloquio:', error);
      const message =
        error.response?.data?.message || 'Errore nell\'eliminazione del colloquio';
      toast.error(message);
    } finally {
      setDeleteLoading(false);
    }
  };

  const formatCandidate = (row) => {
    const fullName = `${row.candidato_cognome || ''} ${row.candidato_nome || ''}`.trim();
    const info = [];
    if (row.candidato_telefono) info.push(row.candidato_telefono);
    if (row.candidato_email) info.push(row.candidato_email);
    return {
      name: fullName || '-',
      details: info.join(' • ')
    };
  };

  const formatStatoLabel = (value) => {
    const normalized = normalizeStatoValue(value);
    const item = statoOptions.find((s) => s.value === normalized);
    return item ? item.label : normalized;
  };

  const formatDateIt = (value) => {
    if (!value) return '';
    const iso = String(value).slice(0, 10);
    const parts = iso.split('-');
    if (parts.length !== 3) return String(value);
    return `${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  const candidatoFilterLabel = useMemo(() => {
    if (!candidatoFilterId) return '';
    const candidate = candidati.find((c) => String(c.id) === String(candidatoFilterId));
    if (!candidate) return `Candidato #${candidatoFilterId}`;
    const name = `${candidate.cognome || ''} ${candidate.nome || ''}`.trim();
    return name || `Candidato #${candidatoFilterId}`;
  }, [candidatoFilterId, candidati]);

  const formatTime = (value) => {
    if (!value) return '-';
    return String(value).slice(0, 5);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 3
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: '50%',
              bgcolor: '#eef2ff',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <HrInterviewsLogoIcon size={20} color="#4f46e5" />
          </Box>
          <Box>
            <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
              Colloqui
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Calendario colloqui, stato esito e note di selezione
            </Typography>
          </Box>
        </Box>
        <Button
          variant="contained"
          color="primary"
          startIcon={<Plus size={18} />}
          onClick={() => {
            returnToCandidatesRef.current = false;
            openCreateDialog();
          }}
        >
          Nuovo colloquio
        </Button>
      </Box>

      <StyledCard>
        <Box
          sx={{
            p: 2,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 2,
            alignItems: 'center'
          }}
        >
          {candidatoFilterId && (
            <Chip
              label={`Candidato: ${candidatoFilterLabel}`}
              size="small"
              variant="outlined"
              onDelete={() => {
                setCandidatoFilterId('');
                setStatoFilter('programmato');
                navigate({ pathname: location.pathname, search: '' }, { replace: true, state: null });
              }}
              sx={{ borderRadius: '10px' }}
            />
          )}
          <TextField
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca per candidato, posizione, note..."
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search size={20} />
                </InputAdornment>
              )
            }}
            sx={{ maxWidth: 360 }}
          />
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="stato-filter-label">Stato</InputLabel>
            <Select
              labelId="stato-filter-label"
              label="Stato"
              value={statoFilter}
              onChange={(e) => setStatoFilter(e.target.value)}
            >
              <MenuItem value="">
                <em>Tutti</em>
              </MenuItem>
              {statoOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel id="fonte-filter-label">Fonte</InputLabel>
            <Select
              labelId="fonte-filter-label"
              label="Fonte"
              value={filterFonteId}
              onChange={(e) => setFilterFonteId(e.target.value)}
            >
              <MenuItem value="">
                <em>Tutte</em>
              </MenuItem>
              {fonti.map((f) => (
                <MenuItem key={f.id} value={f.id}>
                  {f.nome}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <InputLabel id="posizione-filter-label">Posizione</InputLabel>
            <Select
              labelId="posizione-filter-label"
              label="Posizione"
              value={filterPosizioneId}
              onChange={(e) => setFilterPosizioneId(e.target.value)}
            >
              <MenuItem value="">
                <em>Tutte</em>
              </MenuItem>
              {posizioni.map((p) => (
                <MenuItem key={p.id} value={p.id}>
                  {p.titolo}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            size="small"
            type="date"
            label="Dal"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            size="small"
            type="date"
            label="Al"
            value={untilDate}
            onChange={(e) => setUntilDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />
        </Box>

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sortDirection={orderBy === 'candidato_cognome' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'candidato_cognome'}
                    direction={orderBy === 'candidato_cognome' ? order : 'asc'}
                    onClick={() => handleRequestSort('candidato_cognome')}
                  >
                    Candidato
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'posizione_titolo' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'posizione_titolo'}
                    direction={orderBy === 'posizione_titolo' ? order : 'asc'}
                    onClick={() => handleRequestSort('posizione_titolo')}
                  >
                    Posizione
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'fonte_nome' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'fonte_nome'}
                    direction={orderBy === 'fonte_nome' ? order : 'asc'}
                    onClick={() => handleRequestSort('fonte_nome')}
                  >
                    Fonte
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'data_colloquio' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'data_colloquio'}
                    direction={orderBy === 'data_colloquio' ? order : 'asc'}
                    onClick={() => handleRequestSort('data_colloquio')}
                  >
                    Data
                  </TableSortLabel>
                </TableCell>
                <TableCell>Inizio</TableCell>
                <TableCell>Fine</TableCell>
                <TableCell sortDirection={orderBy === 'stato' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'stato'}
                    direction={orderBy === 'stato' ? order : 'asc'}
                    onClick={() => handleRequestSort('stato')}
                  >
                    Stato
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={orderBy === 'esito' ? order : false}>
                  <TableSortLabel
                    active={orderBy === 'esito'}
                    direction={orderBy === 'esito' ? order : 'asc'}
                    onClick={() => handleRequestSort('esito')}
                  >
                    Esito
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Note</TableCell>
                <TableCell align="right">Azioni</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : filteredRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={10} align="center" sx={{ py: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                      Nessun colloquio presente
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                sortedRows
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row) => {
                  const candidate = formatCandidate(row);
                  const rowStato = normalizeStatoValue(row.stato);
                  const rowEsito = normalizeEsitoValue(row.stato || rowStato, row.esito || '');
                  const statoChipColor =
                    rowStato === 'programmato'
                      ? 'warning'
                      : rowStato === 'sostenuto'
                      ? 'info'
                      : rowEsito === 'negativo' || rowEsito === 'offerta_rifiutata'
                      ? 'error'
                      : rowEsito
                      ? 'success'
                      : 'default';

                  return (
                    <TableRow key={row.id} hover>
                      <TableCell>
                        <Typography sx={{ fontWeight: 600 }}>
                          {candidate.name}
                        </Typography>
                        {candidate.details && (
                          <Typography variant="body2" color="text.secondary">
                            {candidate.details}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.posizione_titolo ? (
                          <Chip label={row.posizione_titolo} size="small" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {row.fonte_nome ? (
                          <Chip label={row.fonte_nome} size="small" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>{formatDateIt(row.data_colloquio)}</TableCell>
                      <TableCell>
                        {row.ora_inizio ? String(row.ora_inizio).slice(0, 5) : ''}
                      </TableCell>
                      <TableCell>
                        {row.ora_fine ? String(row.ora_fine).slice(0, 5) : ''}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={formatStatoLabel(rowStato)}
                          size="small"
                          color={statoChipColor}
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell>
                        {rowEsito ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {(() => {
                              if (rowEsito === 'positivo' || rowEsito === 'offerta_fatta') {
                                return <CheckCircle2 size={16} color="#16a34a" />;
                              }
                              if (rowEsito === 'negativo' || rowEsito === 'offerta_rifiutata') {
                                return <XCircle size={16} color="#ef4444" />;
                              }
                              return <FileText size={16} color="#0ea5e9" />;
                            })()}
                            <Typography variant="body2">{formatEsitoLabel(rowEsito)}</Typography>
                          </Box>
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="center">
                        {row.note ? (
                          <FileText size={18} color="#0ea5e9" />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            -
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => openEditDialog(row)}
                          sx={{ mr: 1 }}
                        >
                          <EditIcon size={18} />
                        </IconButton>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => openDeleteDialog(row)}
                        >
                          <DeleteIcon size={18} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={filteredRows.length}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage="Righe per pagina:"
        />
      </StyledCard>

      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (deleteLoading) return;
          setDeleteDialogOpen(false);
          setDeleteTarget(null);
        }}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'rgba(239, 68, 68, 0.10)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <DeleteIcon size={20} color="#ef4444" />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                Elimina colloquio
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Questa operazione non può essere annullata
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
            <Box>
              <Typography variant="subtitle2" color="text.secondary">
                Candidato
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {deleteTarget
                  ? `${deleteTarget.candidato_cognome || ''} ${deleteTarget.candidato_nome || ''}`.trim() || '-'
                  : '-'}
              </Typography>
              {(deleteTarget?.candidato_email || deleteTarget?.candidato_telefono) && (
                <Typography variant="body2" color="text.secondary">
                  {[deleteTarget?.candidato_telefono, deleteTarget?.candidato_email].filter(Boolean).join(' • ')}
                </Typography>
              )}
            </Box>

            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.25 }}>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Data e orario
                </Typography>
                <Typography variant="body2">
                  {formatDateIt(deleteTarget?.data_colloquio) || '-'} • {formatTime(deleteTarget?.ora_inizio)}–{formatTime(deleteTarget?.ora_fine)}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Luogo
                </Typography>
                <Typography variant="body2">{deleteTarget?.luogo || '-'}</Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Posizione / Fonte
                </Typography>
                <Typography variant="body2">
                  {[deleteTarget?.posizione_titolo, deleteTarget?.fonte_nome].filter(Boolean).join(' • ') || '-'}
                </Typography>
              </Box>
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Stato / Esito
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                  <Chip
                    label={formatStatoLabel(deleteTarget?.stato || 'programmato')}
                    size="small"
                    variant="outlined"
                    sx={{ borderRadius: '10px' }}
                  />
                  {(() => {
                    const normalizedEsito = normalizeEsitoValue(
                      deleteTarget?.stato || '',
                      deleteTarget?.esito || ''
                    );
                    if (!normalizedEsito) return null;

                    const chipColor =
                      normalizedEsito === 'positivo' || normalizedEsito === 'offerta_fatta'
                        ? 'success'
                        : normalizedEsito === 'negativo' || normalizedEsito === 'offerta_rifiutata'
                        ? 'error'
                        : 'default';

                    return (
                      <Chip
                        label={formatEsitoLabel(normalizedEsito)}
                        size="small"
                        color={chipColor}
                        variant="outlined"
                        sx={{ borderRadius: '10px' }}
                      />
                    );
                  })()}
                </Box>
              </Box>
            </Box>

            {deleteTarget?.note ? (
              <Box>
                <Typography variant="subtitle2" color="text.secondary">
                  Note
                </Typography>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                  {String(deleteTarget.note).slice(0, 280)}
                  {String(deleteTarget.note).length > 280 ? '…' : ''}
                </Typography>
              </Box>
            ) : null}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteTarget(null);
            }}
            disabled={deleteLoading}
          >
            Annulla
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleteLoading}
            sx={{ borderRadius: '12px' }}
          >
            {deleteLoading ? 'Eliminazione...' : 'Elimina'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={dialogOpen}
        onClose={() => (saving ? null : setDialogOpen(false))}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {editingItem ? 'Modifica colloquio' : 'Nuovo colloquio'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 0.5 }}>
            <Grid item xs={12} md={6}>
              <Autocomplete
                fullWidth
                options={candidati}
                size="small"
                getOptionLabel={(c) => {
                  if (!c) return '';
                  const name = `${c.cognome || ''} ${c.nome || ''}`.trim();
                  const extra = [];
                  if (c.telefono) extra.push(c.telefono);
                  if (c.email) extra.push(c.email);
                  return extra.length ? `${name} (${extra.join(' • ')})` : name;
                }}
                isOptionEqualToValue={(option, value) =>
                  String(option.id) === String(value.id)
                }
                value={
                  candidati.find((c) => String(c.id) === String(candidatoId)) || null
                }
                onChange={(event, newValue) => {
                  setCandidatoId(newValue ? newValue.id : '');
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Candidato" fullWidth />
                )}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="posizione-label">Posizione</InputLabel>
                <Select
                  labelId="posizione-label"
                  label="Posizione"
                  value={posizioneId}
                  onChange={(e) => setPosizioneId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Nessuna</em>
                  </MenuItem>
                  {posizioni.map((p) => (
                    <MenuItem key={p.id} value={p.id}>
                      {p.titolo}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="fonte-label">Fonte</InputLabel>
                <Select
                  labelId="fonte-label"
                  label="Fonte"
                  value={fonteId}
                  onChange={(e) => setFonteId(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Nessuna</em>
                  </MenuItem>
                  {fonti.map((f) => (
                    <MenuItem key={f.id} value={f.id}>
                      {f.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Data colloquio"
                type="date"
                value={dataColloquio}
                onChange={(e) => setDataColloquio(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Ora inizio"
                type="time"
                value={oraInizio}
                onChange={(e) => setOraInizio(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Ora fine"
                type="time"
                value={oraFine}
                onChange={(e) => setOraFine(e.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Luogo"
                value={luogo}
                onChange={(e) => setLuogo(e.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel id="stato-label">Stato</InputLabel>
                <Select
                  labelId="stato-label"
                  label="Stato"
                  value={stato}
                  onChange={(e) => {
                    const next = e.target.value;
                    setStato(next);
                    if (next === 'programmato') {
                      setEsito('');
                    }
                  }}
                >
                  {statoOptions.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {shouldShowEsito && (
              <Grid item xs={12} md={3}>
                <FormControl
                  fullWidth
                  required={stato === 'chiuso'}
                  error={stato === 'chiuso' && submitAttempted && !esito}
                >
                  <InputLabel id="esito-label" required={stato === 'chiuso'}>
                    Esito
                  </InputLabel>
                  <Select
                    labelId="esito-label"
                    label="Esito"
                    value={esito}
                    onChange={(e) => setEsito(e.target.value)}
                  >
                    <MenuItem value="">
                      <em>Seleziona</em>
                    </MenuItem>
                    {esitoOptionsForForm.map((opt) => (
                      <MenuItem key={opt.value} value={opt.value}>
                        {opt.label}
                      </MenuItem>
                    ))}
                  </Select>
                  {stato === 'chiuso' && submitAttempted && !esito && (
                    <FormHelperText>Obbligatorio</FormHelperText>
                  )}
                </FormControl>
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Note"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                fullWidth
                multiline
                minRows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={saving}>
            Annulla
          </Button>
          <Button
            onClick={handleSave}
            variant="contained"
            color="primary"
            disabled={saving}
          >
            {saving ? 'Salvataggio...' : 'Salva'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
